import { useEffect, useState } from "react";
import { getAllWardsForUser, getGenders } from "../../../../services/AppService";
import { resolveMediaUrl } from "../../../../utility/media";
import { IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";

const AddNewMemberForm = ({ onSubmit, editData, onClose, open }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: "",
        gender: "",
        contactPrimary: "",
        panNo: "",
        contactSecondary: "",
        email: "",
        aadhaarNo: "",
        wardName: "",
        active: true
    });
    
    const [preview, setPreview] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [genders, setGenders] = useState([]);
    const [wards, setWards] = useState([]);

    useEffect(() => {
        if (open) {
            getGenders().then(setGenders).catch(console.error);
            getAllWardsForUser().then(setWards).catch(console.error);
        }
    }, [open]);

    useEffect(() => {
        if (editData && open) {
            setFormData({
                name: editData.name || "",
                gender: editData.gender || "",
                contactPrimary: editData.contactPrimary || "",
                panNo: editData.panNo || "",
                contactSecondary: editData.contactSecondary || "",
                email: editData.email || "",
                aadhaarNo: editData.aadhaarNo || "",
                wardName: editData.wardName || "",
                active: editData.active ?? true
            });
            if (editData.photoUrl) {
                setPreview(resolveMediaUrl(editData.photoUrl));
            }
        } else if (open) {
            resetForm();
        }
    }, [editData, open]);

    const resetForm = () => {
        setFormData({ name: "", gender: "", contactPrimary: "", panNo: "", contactSecondary: "", email: "", aadhaarNo: "", wardName: "", active: true });
        setPreview(null);
        setFileName(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const finalValue = name === "active" ? value === "true" : value;
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreview(URL.createObjectURL(file));
            setFileName(file.name);
        }
    };

    const handleRemoveImage = () => {
        setPreview(null);
        setFileName(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const memberDto = {
            ...formData,
            id: editData?.id || null,
        };

        const finalFormData = new FormData();
        finalFormData.append(
            "member",
            new Blob([JSON.stringify(memberDto)], { type: "application/json" })
        );

        // Get file from the specific input name
        const photoInput = e.target.elements.photo;
        if (photoInput && photoInput.files[0]) {
            finalFormData.append("photo", photoInput.files[0]);
        }

        onSubmit(finalFormData);
    };

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1000px', maxWidth: '95%' }}>
                {/* Header matching Plan Estimate */}
                <div className="staff-modal-header">
                    <div className="staff-modal-title">
                        {editData ? t('pageTitles.editMember') : t('pageTitles.addMember')}
                    </div>
                    <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
                </div>

                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.name')} <span>*</span></label>
                                <input 
                                    name="name" 
                                    placeholder={t('forms.member.namePlc')} 
                                    value={formData.name} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.gender')} <span>*</span></label>
                                <select 
                                    name="gender" 
                                    value={formData.gender} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    required
                                >
                                    <option value="">Select Gender</option>
                                    {genders.map((g) => (
                                        <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1).toLowerCase()}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.primaryContact')} <span>*</span></label>
                                <input 
                                    name="contactPrimary" 
                                    placeholder={t('forms.member.primaryContactPlc')}
                                    value={formData.contactPrimary} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    maxLength={10}
                                    onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '')}
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.secondaryContact')}</label>
                                <input 
                                    name="contactSecondary" 
                                    placeholder={t('forms.member.secondaryContactPlc')}
                                    value={formData.contactSecondary} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    maxLength={10}
                                    onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '')}
                                />
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.email')} <span>*</span></label>
                                <input 
                                    name="email" 
                                    type="email" 
                                    placeholder={t('forms.member.emailPlc')}
                                    value={formData.email} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.panNo')} <span>*</span></label>
                                <input 
                                    name="panNo" 
                                    placeholder="Enter PAN No" 
                                    value={formData.panNo} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.aadhaarNo')} <span>*</span></label>
                                <input 
                                    name="aadhaarNo" 
                                    placeholder={t('forms.member.aadhaarNoPlc')}
                                    value={formData.aadhaarNo} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.ward')} <span>*</span></label>
                                <select 
                                    name="wardName" 
                                    value={formData.wardName} 
                                    onChange={handleChange} 
                                    className="staff-modal-input" 
                                    required
                                >
                                    <option value="">Select Ward</option>
                                    {wards.map((w) => (
                                        <option key={w.id} value={w.wardName}>{w.wardName}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('tables.status')}</label>
                                <select 
                                    name="active" 
                                    value={formData.active.toString()} 
                                    onChange={handleChange} 
                                    className="staff-modal-input"
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="staff-modal-label">{t('forms.member.photo')}</label>
                                <input 
                                    name="photo" 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={handleFileChange} 
                                    className="staff-modal-input" 
                                />
                            </div>
                        </div>

                        {preview && (
                            <div className="flex flex-col items-center gap-2 border p-4 rounded-lg bg-gray-50 mb-4 mx-auto w-fit">
                                <img src={preview} alt="Preview" className="w-32 h-32 object-cover rounded border shadow-sm" />
                                <span className="text-xs text-gray-500">{fileName}</span>
                                <button type="button" onClick={handleRemoveImage} className="text-red-500 text-sm font-medium hover:underline">{t('forms.member.removePhoto')}</button>
                            </div>
                        )}

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-12">
                                {editData ? t('forms.member.update') : t('forms.member.save')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddNewMemberForm;